import { useCallback, useEffect, useMemo, useState } from "react";
import { api, ApiRequestError } from "../../api/client";
import {
  BANK_TRANSFER,
  INVOICE_KIND_LABELS,
  PRICING,
  type InvoiceKind,
} from "../../../shared/constants";
import { InvoiceStatusPill, formatDate, personName, yen } from "./ui";

interface AdminInvoice {
  id: string;
  user_id: string;
  user_email: string;
  office_name: string | null;
  case_id: string | null;
  kind: string;
  amount_total: number;
  credit_amount: number | null;
  status: string;
  issued_at: string | null;
  paid_at: string | null;
  created_at: string;
}

interface AdminUser {
  id: string;
  email: string;
  role: string;
  last_name: string | null;
  first_name: string | null;
  office_name: string | null;
}

export function AdminBillingPage() {
  const [invoices, setInvoices] = useState<AdminInvoice[]>([]);
  const [customers, setCustomers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const [statusFilter, setStatusFilter] = useState("all");
  const [kindFilter, setKindFilter] = useState("all");
  const [keyword, setKeyword] = useState("");

  // 新規請求フォーム。
  const [showCreate, setShowCreate] = useState(false);
  const [newUserId, setNewUserId] = useState("");
  const [newKind, setNewKind] = useState<InvoiceKind>("initial");
  const [newAmount, setNewAmount] = useState<number>(PRICING.creditChargeOptions[0]);
  const [createBusy, setCreateBusy] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      api.get<{ invoices: AdminInvoice[] }>("/api/admin/invoices"),
      api.get<{ users: AdminUser[] }>("/api/admin/users"),
    ])
      .then(([iv, us]) => {
        setInvoices(iv.invoices);
        setCustomers(us.users.filter((u) => u.role === "customer"));
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const summary = useMemo(() => {
    const now = new Date();
    const ym = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
    let unpaidCount = 0;
    let unpaidTotal = 0;
    let paidCount = 0;
    let paidTotal = 0;
    let monthSales = 0;
    for (const iv of invoices) {
      if (iv.status === "unpaid") {
        unpaidCount += 1;
        unpaidTotal += iv.amount_total;
      } else if (iv.status === "paid") {
        paidCount += 1;
        paidTotal += iv.amount_total;
        if (iv.paid_at && iv.paid_at.slice(0, 7) === ym) {
          monthSales += iv.amount_total;
        }
      }
    }
    return { unpaidCount, unpaidTotal, paidCount, paidTotal, monthSales };
  }, [invoices]);

  const filtered = useMemo(() => {
    const kw = keyword.trim().toLowerCase();
    return invoices.filter((iv) => {
      if (statusFilter !== "all" && iv.status !== statusFilter) return false;
      if (kindFilter !== "all" && iv.kind !== kindFilter) return false;
      if (kw) {
        const hay = [iv.office_name, iv.user_email, iv.id]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        if (!hay.includes(kw)) return false;
      }
      return true;
    });
  }, [invoices, statusFilter, kindFilter, keyword]);

  const settle = async (iv: AdminInvoice) => {
    if (
      !window.confirm(
        `${yen(iv.amount_total)} の入金を消込します。よろしいですか？`,
      )
    )
      return;
    setBusyId(iv.id);
    setNotice(null);
    setError(null);
    try {
      const res = await api.post<{ credited: number; case_status: string | null }>(
        `/api/admin/invoices/${iv.id}/settle`,
      );
      let msg = "入金を消込しました。";
      if (res.credited) msg += ` ${res.credited.toLocaleString("ja-JP")}cr を付与しました。`;
      if (res.case_status === "intake") msg += " 案件を「ヒアリング中」に更新しました。";
      if (res.case_status === "in_production") msg += " 案件を「制作中」に更新しました。";
      setNotice(msg);
      load();
    } catch (e) {
      setError(e instanceof ApiRequestError ? e.message : "消込に失敗しました");
    } finally {
      setBusyId(null);
    }
  };

  const createInvoice = async () => {
    if (!newUserId) {
      setError("顧客を選択してください");
      return;
    }
    setCreateBusy(true);
    setError(null);
    setNotice(null);
    try {
      const payload: Record<string, unknown> = { user_id: newUserId, kind: newKind };
      if (newKind === "credit_charge") payload.amount = newAmount;
      await api.post("/api/admin/invoices", payload);
      setNotice("請求を作成しました。");
      setShowCreate(false);
      setNewUserId("");
      load();
    } catch (e) {
      setError(e instanceof ApiRequestError ? e.message : "請求作成に失敗しました");
    } finally {
      setCreateBusy(false);
    }
  };

  return (
    <section>
      <div className="admin-page-head">
        <div>
          <div className="admin-eyebrow">運営管理</div>
          <h1>請求・入金管理</h1>
          <p className="muted">
            銀行振込の入金を手動で消込します（免税事業者のため消費税区分なし・税込総額のみ）。
          </p>
        </div>
        <div className="head-actions">
          <button
            className="btn btn-primary btn-sm"
            onClick={() => setShowCreate((v) => !v)}
          >
            請求書を作成
          </button>
        </div>
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      {notice && <div className="alert alert-info">{notice}</div>}

      {showCreate && (
        <div className="card admin-toolbar">
          <label className="filter-field grow">
            <span>顧客</span>
            <select value={newUserId} onChange={(e) => setNewUserId(e.target.value)}>
              <option value="">選択してください</option>
              {customers.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.office_name ?? personName(u.last_name, u.first_name, u.email)}（{u.email}）
                </option>
              ))}
            </select>
          </label>
          <label className="filter-field">
            <span>請求種別</span>
            <select
              value={newKind}
              onChange={(e) => setNewKind(e.target.value as InvoiceKind)}
            >
              {(Object.keys(INVOICE_KIND_LABELS) as InvoiceKind[]).map((k) => (
                <option key={k} value={k}>
                  {INVOICE_KIND_LABELS[k]}
                </option>
              ))}
            </select>
          </label>
          {newKind === "credit_charge" && (
            <label className="filter-field">
              <span>チャージ金額</span>
              <select
                value={newAmount}
                onChange={(e) => setNewAmount(Number(e.target.value))}
              >
                {PRICING.creditChargeOptions.map((a) => (
                  <option key={a} value={a}>
                    {yen(a)}（{a.toLocaleString("ja-JP")}cr）
                  </option>
                ))}
              </select>
            </label>
          )}
          <div className="filter-field">
            <span>&nbsp;</span>
            <button
              className="btn btn-primary btn-sm"
              onClick={createInvoice}
              disabled={createBusy}
            >
              作成
            </button>
          </div>
        </div>
      )}

      <div className="stat-grid">
        <div className="card stat">
          <div className="stat-label">未入金件数</div>
          <div className="stat-value">{loading ? "…" : summary.unpaidCount}</div>
          <div className="stat-label">{yen(summary.unpaidTotal)}</div>
        </div>
        <div className="card stat">
          <div className="stat-label">入金済み件数</div>
          <div className="stat-value">{loading ? "…" : summary.paidCount}</div>
          <div className="stat-label">{yen(summary.paidTotal)}</div>
        </div>
        <div className="card stat">
          <div className="stat-label">今月の売上</div>
          <div className="stat-value stat-value-sm">
            {loading ? "…" : yen(summary.monthSales)}
          </div>
          <div className="stat-label">税込総額</div>
        </div>
        <div className="card stat">
          <div className="stat-label">未入金合計</div>
          <div className="stat-value stat-value-sm">
            {loading ? "…" : yen(summary.unpaidTotal)}
          </div>
          <div className="stat-label">回収予定</div>
        </div>
      </div>

      <div className="card admin-toolbar">
        <label className="filter-field grow">
          <span>キーワード検索</span>
          <input
            type="text"
            placeholder="事務所名・メール・請求IDで検索"
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
          />
        </label>
        <label className="filter-field">
          <span>ステータス</span>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="all">すべて</option>
            <option value="unpaid">未入金</option>
            <option value="paid">入金済み</option>
            <option value="canceled">取消</option>
          </select>
        </label>
        <label className="filter-field">
          <span>種別</span>
          <select value={kindFilter} onChange={(e) => setKindFilter(e.target.value)}>
            <option value="all">すべて</option>
            {(Object.keys(INVOICE_KIND_LABELS) as InvoiceKind[]).map((k) => (
              <option key={k} value={k}>
                {INVOICE_KIND_LABELS[k]}
              </option>
            ))}
          </select>
        </label>
      </div>

      <div className="card">
        {loading ? (
          <p className="muted">読み込み中...</p>
        ) : filtered.length === 0 ? (
          <p className="muted">該当する請求がありません。</p>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>請求ID</th>
                <th>事務所 / 顧客</th>
                <th>内容</th>
                <th>金額（税込）</th>
                <th>ステータス</th>
                <th>請求日</th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((iv) => (
                <tr key={iv.id}>
                  <td className="mono small">{iv.id.slice(0, 8)}</td>
                  <td>
                    <div className="cell-strong">{iv.office_name ?? "—"}</div>
                    <div className="muted small">{iv.user_email}</div>
                  </td>
                  <td>
                    {INVOICE_KIND_LABELS[iv.kind as InvoiceKind] ?? iv.kind}
                    {iv.kind === "credit_charge" && iv.credit_amount
                      ? `（${iv.credit_amount.toLocaleString("ja-JP")}cr）`
                      : ""}
                  </td>
                  <td>{yen(iv.amount_total)}</td>
                  <td>
                    <InvoiceStatusPill status={iv.status} />
                  </td>
                  <td>{formatDate(iv.issued_at ?? iv.created_at)}</td>
                  <td>
                    {iv.status === "unpaid" ? (
                      <button
                        className="btn btn-primary btn-sm"
                        onClick={() => settle(iv)}
                        disabled={busyId === iv.id}
                      >
                        入金消込
                      </button>
                    ) : (
                      <span className="muted small">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="card">
        <h2>振込先口座情報</h2>
        <div className="bank-box">
          <dl>
            <dt>金融機関</dt>
            <dd>
              {BANK_TRANSFER.bankName}（{BANK_TRANSFER.bankCode}）
            </dd>
            <dt>支店</dt>
            <dd>
              {BANK_TRANSFER.branchName}（{BANK_TRANSFER.branchCode}）
            </dd>
            <dt>口座</dt>
            <dd>
              {BANK_TRANSFER.accountType} {BANK_TRANSFER.accountNumber}
            </dd>
            <dt>名義</dt>
            <dd>{BANK_TRANSFER.accountHolder}</dd>
          </dl>
        </div>
      </div>
    </section>
  );
}
